'use client';

import React from 'react';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eliadmin/core/src/components/ui/tooltip';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type SocialIconsProps = {
  className?: {
    wrapper?: string;
    iconLink?: string;
    icon?: string;
    button?: string;
  };
  showTooltip?: boolean;
};

export function SocialIcons({ className, showTooltip = false }: SocialIconsProps) {
  const generalLayout = useGlobalStore(s => s.generalLayout);

  const formatSocialMediaName = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

  const renderButton = (key: string, value: string) => (
    <Button
      asChild
      variant="outline"
      aria-label={`Social media link ${formatSocialMediaName(key)}`}
      size="icon"
      className={cn('ea-social-icons__button', className?.button)}
    >
      <span data-ea-visual-tag="button" style={{ textAlign: 'center' }}>
        <a
          href={value}
          aria-label={formatSocialMediaName(key)}
          target="_blank"
          rel="noreferrer"
          className={cn('ea-social-icons__icon-link', className?.iconLink)}
        >
          {React.cloneElement(
            HEADER_CONFIG.socialMediaIcons[key as keyof GeneralLayout['data']['social_media']],
            {
              className: cn('h-6 w-6', 'ea-social-icons__icon', className?.icon),
            },
          )}
        </a>
      </span>
    </Button>
  );

  return (
    <TooltipProvider>
      <div
        className={cn(
          'flex flex-row flex-wrap gap-2',
          'ea-social-icons__wrapper',
          className?.wrapper,
        )}
      >
        {generalLayout.social_media &&
          Object.entries(generalLayout.social_media)
            .filter(([_, value]) => !!value)
            .map(([key, value]) =>
              showTooltip ? (
                <Tooltip key={key} delayDuration={0}>
                  <TooltipTrigger asChild>{renderButton(key, value)}</TooltipTrigger>
                  <TooltipContent>
                    <p>{formatSocialMediaName(key)}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <React.Fragment key={key}>{renderButton(key, value)}</React.Fragment>
              ),
            )}
      </div>
    </TooltipProvider>
  );
}
